import { DashInstance } from '@/store'
import { toFixed, toInteger, toFloat } from './number.utils'
import TextManager from './text-manager.utils'
class MathManager {
  /**
   * sumByKeys is used to sum the values of the specified keys in a list object,
   * which is assumed to be an instance variable of the class that this function is defined in.
   * The function uses the reduce method to iterate through the list object, adding the values of the specified keys
   *  together and returning a new object with the summed values for those keys.
   *
   * The result is stored back in the list object and the function returns this to allow for method chaining.
   * @param {Array} items list sumed
   * @param {Array} keys list of properties keys to be summed
   * @returns new object with the sum of the last items
   */
  static sumByKeys (items = [], keys = ['quantity']) {
    if (items.length <= 0) return []

    const sum = {}
    keys.forEach(key => {
      sum[key] = items.reduce((acc, curr) => {
        if (key in curr) {
          return acc + curr[key]
        } else {
          return acc
        }
      }, 0)
    })

    return sum
  }

  /**
   * Calculates the overall conversion rate using weighted averages.
   *
   * @param {number[]} participations - An array of participation values for each category.
   * @param {number[]} rates - An array of conversion rates corresponding to each category.
   * @returns {number} - The overall weighted conversion rate.
   * @throws {Error} - If the lengths of the `participations` and `rates` arrays are not equal.
   */
  static calculateConversionRate (participations, rates) {
    if (participations.length !== rates.length) {
      throw new Error(
        'The participation and rate arrays must have the same length.'
      )
    }

    if (participations.length === 0 || rates.length === 0) {
      return 0
    }

    let weightedSum = 0
    let totalParticipations = 0

    for (let i = 0; i < participations.length; i++) {
      weightedSum += participations[i] * rates[i]
      totalParticipations += participations[i]
    }

    return weightedSum / totalParticipations
  }

  /**
   * convertToMonetary is used to divide by 100 due to the amount of decimal places returned in the bff
   * api for monetary values
   * @param {number} value monetary number returned by bff api
   * @returns the value divide by 100
   */
  static convertToMonetary (value) {
    return value / 100 || 0
  }

  static percentageCalculator (value = 0, total = 100) {
    return DashInstance.commons.percentageCalculator(total, value)
  }

  static percentageToNumber (value) {
    if (!value) return 0
    if (typeof value === 'number') return value / 100
    if (!value.includes('%'))
      throw new Error(`Value provided ${value} is not a percentage!`)

    const number = +TextManager.replaceAllValuesObject(value, {
      ',': '.',
      '%': ''
    })

    return Number(number) / 100
  }

  static formatCurrencyLegacy (value) {
    const formatter = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    })
    return formatter.format(value)
  }

  static formatCurrencyBRL (value = 0) {
    return DashInstance.commons.formatCurrencyBRL(value)
  }

  static formatCurrencySymbol (value, symbol = 'R$') {
    return `${symbol} ${DashInstance.commons.formatCurrencyBRL(value)}`
  }

  /**
   * replace /\s/ (unicode) for space
   * @param {Number} value
   * @param {String} currency BRL
   * @param {String} lang pt-B
   * @returns String currency formated
   */
  static formatCurrency (value = 0, currency = 'BRL', lang = 'pt-BR') {
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency
    })
      .format(value)
      .replace(/\s/, ' ')
  }

  static convertCentsToBRL (value) {
    return DashInstance.commons.convertCentsToBRL(value)
  }

  static formatConversionNumber (value) {
    return DashInstance.insights.formatConversionNumber(value)
  }

  /**
   * Convert number in decimal number extesion
   * ex. input: 2000 - output 2.000
   * @param {Number} value
   * @param {String} lang pt-BR
   * @returns Number
   */
  static formatNumberDecimal (value, lang = 'pt-BR') {
    return new Intl.NumberFormat(lang).format(value)
  }

  static numberBase (value) {
    const [coefficient, exponent] = value
      .toExponential()
      .split('e')
      .map(item => Number(item))

    return [coefficient, exponent]
  }

  static largestNumberInArray (array) {
    return array.reduce((a, b) => Math.max(a, b))
  }

  /**
   * @description Converts the value into a fixed float
   * @param valueToConvert the value to be converted into a fixed float
   */
  static getValue (valueToConvert, precisionValue) {
    return toFixed(toFloat(valueToConvert, 0), precisionValue)
  }

  /**
   * @description Validates the max value and defaults to 100 for invalid values 0, nan, -number
   * @param valuetoconvert the value to be converted into a fixed float
   */
  static getMax (valueToConvert) {
    const _max = toFloat(valueToConvert)
    return _max > 0 ? _max : 100
  }

  /**
   * @description Defaults to 0 for invalid values -number, NaN
   * @param precisionValue the value for the precision @default 0
   */
  static getPrecision (precisionValue = 0) {
    return Math.max(toInteger(precisionValue, 0), 0)
  }

  /**
   * @description Calculates the progress value, max value and precision value into the correct processed value
   * @param progressValue the value for the progress
   * @param maxValue the max value of the progress bat
   * @param precisionValue the precision value @default 0
   */
  static getProgress (progressValue, maxValue, precisionValue = 0) {
    const _precision = MathManager.getPrecision(precisionValue)
    const pow = Math.pow(10, _precision)
    return toFixed(
      (100 * pow * MathManager.getValue(progressValue)) /
        MathManager.getMax(maxValue) /
        pow,
      _precision
    )
  }

  /**
   * Parse a localized number to a float.
   * @param {string} currency - the localized number
   * @param {string} currencySymbol - the currency symbol to be replaced
   * @param {string} thousandsSeparator - thousands digit separator
   * @param {string} decimalSeparator - decimal digits separator
   */
  static parseCurrencyToNumber (
    currency,
    currencySymbol = 'R$',
    thousandsSeparator = '.',
    decimalSeparator = ','
  ) {
    let charAt, temp
    const NUMERIC_START = 47
    const NUMERIC_END = 58
    const MINUS_CHAR = 45
    let isNegative = false

    // Loop to make substring
    for (let i = 0; i < currency.length; i++) {
      // Getting Unicode value
      charAt = currency.charCodeAt(i)

      // Checking whether the character
      // is of numeric type or not
      if (charAt > NUMERIC_START && charAt < NUMERIC_END) {
        // Making substring
        temp = currency.substring(i)
        break
      }

      // Check if the character is a minus sign
      if (charAt === MINUS_CHAR) {
        isNegative = true
      }
    }

    // If currency is in format like
    // 458, 656.75 then we used replace

    // method to replace every thousands and decimals
    temp = temp.replace(currencySymbol, '')
    temp = temp.replace(thousandsSeparator, '')
    temp = temp.replace(decimalSeparator, '.')
    temp = temp.trim()

    // Converting string to float
    // or double and return
    const parsedValue = parseFloat(temp)

    // If the value is negative, return the negative value
    return isNegative ? -parsedValue : parsedValue
  }
}

export default MathManager
